//
import React, { useState, useCallback } from 'react';

import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Radio from '@mui/material/Radio';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { accountDiscoveryActions } from 'app-components/flux/account-discovery';
import { accountsActions, accountsTypes, accountsUtils } from 'app-components/flux/accounts';
// import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { housingSelectionSelectors } from 'app-components/flux/housing-selection';
import { rootUiActions } from 'app-components/flux/root-ui';
import { transactionsActions, transactionsTypes } from 'app-components/flux/transactions';
import { loadState, useLoadState } from 'app-components/hooks/utils';
import car from 'assets/icons/asset-discovery/car.svg';
import paperAirplaneCrash from 'assets/illustrations/Plane-Crash.png';
import StdDialog from 'components/Dialogs/StdDialog';
import StdDialogContent from 'components/Dialogs/StdDialogContent';
import StdDialogForm from 'components/Dialogs/StdDialogForm';
import QAmountField from 'components/QAmountField';
import QButton from 'components/QButton';
import ZeroStateView from 'components/ZeroStateView';
import { Field, Form, Formik } from 'formik';
import { DateTime } from 'luxon';
import { useDispatch } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuidv4 } from 'uuid';
import LoadingInfo from '../LoadingInfo';
import TypeSelector from '../TypeSelector';
import { DIALOG_TYPE_ASSET, showVehicleDialog } from '../actions';
import KBBStats from './KBBStats';

const useStyles = makeStyles()(() => ({
  root: {
    position: 'relative',
    minHeight: 450,
  },
  accountName: {
    marginTop: 12,
    width: 550,
  },
  addressBoxes: {
    marginRight: 16,
    marginTop: 12,
    width: 550,
  },
  accountTypeFormControl: {
    marginTop: 12,
    paddingBottom: 12,
    width: 550,
  },
  headlineRoot: {
    padding: '10px 60px 10px 150px',
  },
  headlineIcon: {
    position: 'absolute',
    height: 135,
    top: 30,
    left: 0,
  },
  headlineBigIcon: {
    position: 'absolute',
    height: 150,
    top: 30,
    left: 0,
  },
  assetText: {
    fontSize: 24,
  },
  assetStyling: {
    display: 'flex',
    maxWidth: 460,
  },
}));

// TODO asset needs to be set from a QCS call to kbb
const asset = {
  name: '2021 MAZDA CX-30',
  type: 'SUV',
  specType: 'Premium Sport Utility 4D',
  engine: '4-Cyl, SKYACTIV-G, 2.5 Liter',
  wheels: 'AWD',
  vin: '3MVDMBDL7MM205399',
};

const VehicleDialog = (_props) => {
  const dispatch = useDispatch();
  const [state, setState] = useState('');
  const [vehicle, setVehicle] = useState(asset);
  const [kbbUpdate, setKbbUpdate] = useState(true);
  const [accountNameQCS, setAccountNameQCS] = useState('');
  const [amount, setAmountInternal] = useState(Number(0));
  const { classes } = useStyles();

  const showManualFlow = true; // TODO CHANGE THIS WHEN KELLY BLUE BOOK IS DONE

  const refreshData = useCallback(() => {
    dispatch(showVehicleDialog());
  }, [dispatch]);

  const updateAccount = useCallback(
    (e) => {
      const newName = e.target.value;
      setAccountNameQCS(newName);
    },
    [setAccountNameQCS],
  );

  const updateAmount = useCallback(
    (e) => {
      const newAmount = Number(e.target.value);
      setAmountInternal(newAmount);
    },
    [setAmountInternal],
  );

  const onClose = useCallback(() => {
    dispatch(rootUiActions.removeDialog(DIALOG_TYPE_ASSET));
  }, [dispatch]);

  const assetDetailsList = {
    value: 440023,
  };
  asset.assetDetailsList = assetDetailsList;
  // replace this housingSelectionSelectors with the selector for kbb stats or replace it in kbb stats
  const housingSelectionView = useLoadState(housingSelectionSelectors);

  const showTryAgainView =
    loadState.tryAgainStates.includes(housingSelectionView) ||
    loadState.updateRequiredStates.includes(housingSelectionView);
  const showNoDataView = loadState.noDataStates.includes(housingSelectionView);
  const showReadyView = loadState.readyStates.includes(housingSelectionView);

  // //  Ordered by priority
  let mainView = '';
  if (showReadyView) {
    mainView = 'READY';
  } else if (showTryAgainView) {
    mainView = 'FAILED';
  } else if (showNoDataView) {
    mainView = 'EMPTY';
  }
  if (mainView === 'FAILED' && state !== 'error') {
    setState('error');
  }
  mainView = 'READY';

  const handleConfirm = (values, manualAccount) => {
    const accountParams = {
      // clientId: uuidv4().toUpperCase(),
      // name: accountNameQCS.trim(),
      // kbbId: manualAccount ? null : vehicle.name,
      // kbbUpdate: manualAccount ? false : kbbUpdate,
      // subType: values.accountType.qcsType,
      // type: 'VEHICLE',
      // balanceAsOf: 0.0,
      // balanceAsOfOn: '1970-01-01', // TODO ADD BACK WHEN KELLY BLUE BOOK IS READY

      clientId: uuidv4().toUpperCase(),
      institutionLoginId: undefined,
      isExcludedFromAccountBar: false,
      isExcludedFromBudgets: true,
      isExcludedFromReports: true,
      name: accountNameQCS.trim(),
    };
    const accountToUpsert = accountsTypes.mkAccount('VEHICLE', accountParams);

    const openingBalanceAsOf = (amount && Number(amount.toFixed(2))) || 0.0;
    const openingBalanceAsOfOn = DateTime.local().toISODate() || '1970-01-01';
    new Promise((resolve, reject) => {
      dispatch(accountsActions.batchAccounts([accountToUpsert], { resolve, reject }));
    })
      .then((response) => {
        if (openingBalanceAsOf !== 0.0) {
          const amountSign = accountsUtils.balanceSignForType(values?.accountType?.qcsType);
          const accountId = response?.data?.items?.[0]?.resource?.id;
          if (accountId) {
            const newTxn = new transactionsTypes.CashFlowTransaction({
              accountId,
              payee: manualAccount ? `Car Value ${values.statementDate}` : `KBB Estimate ${values.statementDate}`,
              coa: {
                type: 'BALANCE_ADJUSTMENT',
                id: '0',
              },
              state: 'RECONCILED',
              isReviewed: true,
              amount: openingBalanceAsOf * amountSign,
              postedOn: openingBalanceAsOfOn,
              clientId: uuidv4().toUpperCase(),
              isExcludedFromReports: true,
              isExcludedFromF2S: true,
            });
            dispatch(transactionsActions.batchTransactions([newTxn], { context: 'addAccounts' }));
            onClose();
          }
        }
      })
      .catch(() => {
        onClose();
      });
  };

  const onBack = () => {
    switch (state) {
      case 'sentReq':
        setState('');
        setVehicle({});
        break;
      case 'stats':
        setState('');
        break;
      case 'confirm':
        setState('stats');
        break;
      case 'manualAcc':
        setAmountInternal(assetDetailsList.value);
        setState('stats');
        break;
      default:
        onClose();
        dispatch(accountDiscoveryActions.doAccountDiscovery({ initiator: 'account list dashboard', mode: 'ADD-FI' }));
        break;
    }
  };

  const renderForm = (formikBag) => {
    const { errors, touched, values } = formikBag;

    const SubmitReq = () => {
      setState('sentReq');
      setTimeout(() => {
        setState('stats');
        setAmountInternal(440023);
      }, 1000);
      // TODO update the query for how the kbb request works
      // const queryParam = `line1 == "${values.streetName}",line2 == "${values.aptNum}",state == "",zipCode == "${values.zipCode}"`;
      // // TODO update this for new kbb, no need for housing selection anymore and maybe not even using this
      // actionHelpers.bindPromiseAction(dispatch, housingSelectionActions.getHousingSelection)(undefined, { axiosConfig: { params: { query: queryParam } } })
      //   .then(() => {
      //     setState('recievedData');
      //   });
    };
    const error = errors.accountName && touched.accountName ? errors.accountName : null;

    if (state === '' && !showManualFlow) {
      setKbbUpdate(true);
      setAccountNameQCS('');
      setAmountInternal(Number(0));
      return (
        <StdDialogForm component={Form}>
          <StdDialogContent>
            <DialogContent className={classes.root}>
              <img className={classes.headlineIcon} src={car} alt='' />
              <Grid className={classes.headlineRoot} item container>
                <div>
                  <Typography className={classes.assetText}>Lets find your car</Typography>
                  <div>
                    <Field name='vehicle.isVIN'>
                      {({ field }) => (
                        <FormControlLabel
                          {...field}
                          control={<Radio />}
                          label='VIN'
                          id='VIN-checkbox'
                          checked={Boolean(field.value)}
                          onChange={(event, checked) => {
                            field.onChange(event, Boolean(checked));
                            if (checked) {
                              formikBag.setFieldValue('vehicle.isMakeModel', false);
                              formikBag.setFieldValue('vehicle.isPlate', false);
                              formikBag.setFieldValue('vehicle.isVIN', true);
                            }
                          }}
                        />
                      )}
                    </Field>
                    <Field name='vehicle.isMakeModel'>
                      {({ field }) => (
                        <FormControlLabel
                          {...field}
                          control={<Radio />}
                          label='Make/Model'
                          id='MakeModel-checkbox'
                          checked={Boolean(field.value)}
                          onChange={(event, checked) => {
                            field.onChange(event, Boolean(checked));
                            if (checked) {
                              formikBag.setFieldValue('vehicle.isMakeModel', true);
                              formikBag.setFieldValue('vehicle.isPlate', false);
                              formikBag.setFieldValue('vehicle.isVIN', false);
                            }
                          }}
                        />
                      )}
                    </Field>
                    <Field name='vehicle.isPlate'>
                      {({ field }) => (
                        <FormControlLabel
                          {...field}
                          control={<Radio />}
                          label='Plate'
                          id='Plate-checkbox'
                          checked={Boolean(field.value)}
                          onChange={(event, checked) => {
                            field.onChange(event, Boolean(checked));
                            if (checked) {
                              formikBag.setFieldValue('vehicle.isMakeModel', false);
                              formikBag.setFieldValue('vehicle.isPlate', true);
                              formikBag.setFieldValue('vehicle.isVIN', false);
                            }
                          }}
                        />
                      )}
                    </Field>
                  </div>
                </div>
                {values.vehicle?.isVIN && (
                  <Field
                    name='VIN'
                    label='VIN'
                    variant='outlined'
                    className={classes.accountName}
                    inputProps={{
                      id: 'vin',
                    }}
                    validate={(value) => (value && value.length > 0 ? undefined : 'required')}
                  >
                    {({ field }) => (
                      <TextField
                        {...field}
                        className={classes.accountName}
                        label='VIN'
                        InputProps={{
                          autoComplete: 'off',
                          maxLength: 64,
                          inputProps: {
                            maxLength: 64,
                          },
                        }}
                        variant='outlined'
                        margin='normal'
                        fullWidth
                        error={!!error}
                        helperText={error}
                      />
                    )}
                  </Field>
                )}
                {values.vehicle?.isPlate && (
                  <div className={classes.assetStyling}>
                    <Field
                      name='licensePlate'
                      label='License Plate'
                      variant='outlined'
                      className={classes.accountName}
                      style={{ marginRight: 16 }}
                      inputProps={{
                        id: 'licensePlate',
                      }}
                      validate={(value) => (value && value.length > 0 ? undefined : 'required')}
                    >
                      {({ field }) => (
                        <TextField
                          {...field}
                          className={classes.addressBoxes}
                          label='License Plate'
                          InputProps={{
                            autoComplete: 'off',
                            maxLength: 64,
                            inputProps: {
                              maxLength: 64,
                            },
                          }}
                          variant='outlined'
                          margin='normal'
                          fullWidth
                          error={!!error}
                          helperText={error}
                        />
                      )}
                    </Field>
                    <Field
                      name='state'
                      label='State'
                      variant='outlined'
                      className={classes.accountName}
                      inputProps={{
                        id: 'state',
                      }}
                    >
                      {({ field }) => (
                        <TextField
                          {...field}
                          className={classes.accountName}
                          label='State'
                          InputProps={{
                            autoComplete: 'off',
                            maxLength: 64,
                            inputProps: {
                              maxLength: 64,
                            },
                          }}
                          variant='outlined'
                          margin='normal'
                          fullWidth
                          error={!!error}
                          helperText={error}
                        />
                      )}
                    </Field>
                  </div>
                )}
                {values.vehicle?.isMakeModel && (
                  <div className={classes.assetStyling}>
                    <Field
                      name='make'
                      label='Make'
                      variant='outlined'
                      className={classes.accountName}
                      style={{ marginRight: 16 }}
                      inputProps={{
                        id: 'make',
                      }}
                      validate={(value) => (value && value.length > 0 ? undefined : 'required')}
                    >
                      {({ field }) => (
                        <TextField
                          {...field}
                          className={classes.addressBoxes}
                          label='Make'
                          InputProps={{
                            autoComplete: 'off',
                            maxLength: 64,
                            inputProps: {
                              maxLength: 64,
                            },
                          }}
                          variant='outlined'
                          margin='normal'
                          fullWidth
                          error={!!error}
                          helperText={error}
                        />
                      )}
                    </Field>
                    <Field
                      name='model'
                      label='Model'
                      variant='outlined'
                      className={classes.accountName}
                      inputProps={{
                        id: 'model',
                      }}
                    >
                      {({ field }) => (
                        <TextField
                          {...field}
                          className={classes.accountName}
                          label='Model'
                          InputProps={{
                            autoComplete: 'off',
                            maxLength: 64,
                            inputProps: {
                              maxLength: 64,
                            },
                          }}
                          variant='outlined'
                          margin='normal'
                          fullWidth
                          error={!!error}
                          helperText={error}
                        />
                      )}
                    </Field>
                  </div>
                )}
              </Grid>
            </DialogContent>
          </StdDialogContent>
          <DialogActions>
            <QButton
              id='continue-button'
              disabled={
                (!values.vehicle?.isVIN && !values.vehicle?.isPlate) ||
                (values.vehicle?.isVIN && !values.VIN) ||
                (values.vehicle?.isPlate && !values.licensePlate)
              }
              variant='contained'
              onClick={() => SubmitReq()}
            >
              Continue
            </QButton>
          </DialogActions>
        </StdDialogForm>
      );
    }
    if (state === 'sentReq') {
      return <LoadingInfo realestate={false} />;
    }
    if (state === 'stats') {
      return (
        <KBBStats
          asset={vehicle}
          setState={setState}
          kbbUpdate={kbbUpdate}
          setKbbUpdate={setKbbUpdate}
          setAccountNameQCS={setAccountNameQCS}
          amount={amount}
          setAmountInternal={setAmountInternal}
        />
      );
    }
    if (state === 'manualAcc' || showManualFlow) {
      return (
        <StdDialogForm component={Form}>
          <StdDialogContent>
            <Typography variant='titleMedium' className={classes.subtitle}>
              Enter vehicle details
            </Typography>
            <Field
              name='accountNameQCS'
              label='Account Name'
              variant='outlined'
              className={classes.accountName}
              inputProps={{
                id: 'accountName',
              }}
              validate={(value) => (value && value.length > 0 ? undefined : 'required')}
            >
              {({ field }) => (
                <TextField
                  {...field}
                  className={classes.accountName}
                  label='Account Name'
                  name='accountNameQCS'
                  value={accountNameQCS}
                  onChange={updateAccount}
                  InputProps={{
                    autoComplete: 'off',
                    maxLength: 32,
                    inputProps: {
                      maxLength: 32,
                    },
                  }}
                  variant='outlined'
                  margin='normal'
                  fullWidth
                  error={!!error}
                  helperText={error}
                />
              )}
            </Field>
            <Field
              name='vehicleType'
              label='Account Type'
              variant='outlined'
              className={classes.accountName}
              inputProps={{
                id: 'manual_account_name',
              }}
            >
              {({ field }) => (
                <TextField
                  {...field}
                  className={classes.accountName}
                  id='manual_account_name-field'
                  label='Account Type'
                  defaultValue='Vehicle'
                  disabled
                  InputProps={{
                    autoComplete: 'off',
                    maxLength: 64,
                    inputProps: {
                      maxLength: 64,
                    },
                  }}
                  variant='outlined'
                  margin='normal'
                  fullWidth
                  error={!!error}
                  helperText={error}
                />
              )}
            </Field>
            {/* <div className={classes.accountTypeFormControl}>
              <TypeSelector
                fullWidth
                id="manual_account_type"
                name="accountType"
              />
            </div> */}
            <QAmountField
              variant='outlined'
              editable
              noNegatives
              value={amount}
              onChange={updateAmount}
              className={classes.accountName}
              id='schedtxn-amount'
              textFieldVariant={'outlined'}
              label={'Estimated Value'}
              autoComplete='one-time-code'
              InputProps={{ id: 'manual_ending_balance' }}
            />
          </StdDialogContent>
          <DialogActions>
            <QButton
              id='continue-btn'
              variant='contained'
              onClick={() => handleConfirm(values, true)}
              disabled={!amount || !accountNameQCS}
            >
              Confirm
            </QButton>
          </DialogActions>
        </StdDialogForm>
      );
    }
    if (state === 'confirm') {
      return (
        <StdDialogForm component={Form}>
          <StdDialogContent>
            <DialogContent className={classes.root}>
              <img className={classes.headlineBigIcon} src={car} alt='' />
              <Grid className={classes.headlineRoot} item container>
                <Typography className={classes.assetText}>Confirm vehicle details</Typography>
                <Field
                  name='accountNameQCS'
                  label='Account Name'
                  variant='outlined'
                  className={classes.accountName}
                  inputProps={{
                    id: 'manual_account_name',
                  }}
                  validate={(value) => (value && value.length > 0 ? undefined : 'required')}
                >
                  {({ field }) => (
                    <TextField
                      {...field}
                      className={classes.accountName}
                      label='Account Name'
                      name='accountNameQCS'
                      value={accountNameQCS}
                      onChange={updateAmount}
                      InputProps={{
                        autoComplete: 'off',
                        maxLength: 64,
                        inputProps: {
                          maxLength: 64,
                        },
                      }}
                      variant='outlined'
                      margin='normal'
                      fullWidth
                      error={!!error}
                      helperText={error}
                    />
                  )}
                </Field>
                <div className={classes.accountTypeFormControl}>
                  <TypeSelector fullWidth id='manual_account_type' name='accountType' showManualTypes />
                </div>
              </Grid>
            </DialogContent>
          </StdDialogContent>
          <DialogActions>
            <QButton
              id='confirm-button'
              variant='contained'
              disabled={accountNameQCS?.length === 0}
              onClick={() => handleConfirm(values, false)}
            >
              Confirm
            </QButton>
          </DialogActions>
        </StdDialogForm>
      );
    }
    return null;
  };
  // const initialVals = { accountNameQCS: '', VIN: '', licensePlate: '', statementDate: DateTime.local().toFormat('D'), state: '', make: '', model: '', vehicle: { isVIN: true }, accountType: intial };
  const initialVals = { accountNameQCS: '' };

  return (
    <StdDialog
      title='Add a Vehicle'
      onClose={onClose}
      size='md'
      onBack={onBack}
      showBackButton
      dialogId={'vehicle-dialog'}
    >
      {mainView === 'FAILED' ? (
        <ZeroStateView
          className={classes.errorTab}
          button={
            <QButton id={'try-again-button'} variant='outlined' onClick={refreshData}>
              TRY AGAIN
            </QButton>
          }
          id='failed-to-fetch'
          primary='Unable to fetch data'
          secondary="We can't fetch your data from the server. Please try again"
          icon={paperAirplaneCrash}
        />
      ) : (
        <Formik initialValues={initialVals}>{renderForm}</Formik>
      )}
    </StdDialog>
  );
};

export default VehicleDialog;
